import DOMPurify from 'dompurify';
import { buildCanonicalInlineImageUrl, extractKeyFromImageUrl, resolveInlineImageUrl, getCachedInlineBlobUrl } from './attachmentUtils';

const ALLOWED_TAGS = [
  'a', 'abbr', 'b', 'blockquote', 'br', 'code', 'col', 'colgroup', 'del', 'div',
  'em', 'figcaption', 'figure', 'font', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'i', 'img', 'li', 'ol', 'p',
  'pre', 's', 'span', 'strong', 'sub', 'sup', 'table', 'tbody', 'td', 'tfoot',
  'th', 'thead', 'tr', 'u', 'ul'
];

const ALLOWED_ATTR = [
  'align', 'aria-label', 'class', 'colspan', 'contenteditable', 'data-align', 'data-border-style',
  'data-file-key', 'data-header-column', 'data-header-row', 'data-mention-exit',
  'data-original-code', 'data-placeholder', 'data-pmpi-created-table', 'data-pmpi-raw-html',
  'data-pmpi-row-numbers', 'data-pmpi-table', 'data-screenshot-id', 'data-uploaded-url',
  'data-user-id', 'data-width-percent', 'dir', 'height',
  'alt', 'color', 'face', 'href', 'lang', 'rel', 'rowspan', 'scope', 'size',
  'src', 'start', 'style', 'target', 'title', 'width'
];


const SAFE_CLASSES = new Set([
  'comment-box-image-paste-error', 'mention', 'mention-exit', 'pmpi-code-block',
  'pmpi-created-table', 'pmpi-rich-table',
  'pmpi-inline-screenshot', 'pmpi-screenshot-image-wrap', 'pmpi-screenshot-img',
  'pmpi-screenshot-caption', 'pmpi-screenshot-active', 'pmpi-screenshot-resize-handle',
  'pmpi-screenshot-handle-left', 'pmpi-screenshot-handle-right', 'is-loading'
]);

const BASE_STYLE_PROPERTIES = new Set([
  'background-color', 'border', 'border-bottom', 'border-color', 'border-left',
  'border-radius', 'border-right', 'border-style', 'border-top', 'border-width',
  'color', 'display', 'font-family', 'font-size', 'font-style', 'font-weight',
  'height', 'line-height', 'margin', 'margin-bottom', 'margin-left', 'margin-right',
  'margin-top', 'max-width', 'min-width', 'padding', 'padding-bottom',
  'padding-left', 'padding-right', 'padding-top', 'text-align', 'text-decoration',
  'vertical-align', 'white-space', 'width', 'word-break', 'overflow-wrap'
]);

const TABLE_STYLE_PROPERTIES = new Set([
  ...BASE_STYLE_PROPERTIES, 'border-collapse', 'border-spacing', 'table-layout'
]);

const UNSAFE_STYLE_VALUE = /url\s*\(|expression\s*\(|javascript\s*:|@import|-moz-binding|behavior\s*:/i;

export const SAFE_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/avif'];

export const ALLOWED_DATA_IMAGE_REGEXP = /^data:image\/(?:png|jpe?g|gif|webp|avif)(?:;[a-z0-9-]+=(?:"[^"]*"|[a-z0-9-_]+))*(?:;base64)?,/i;

export const ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto|tel|blob):|cid:|\/|#|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i;

export const isAllowedMediaSrc = (src) => {
  if (typeof src !== 'string') return false;
  const trimmed = src.trim();
  if (!trimmed) return false;

  const schemeMatch = trimmed.match(/^([a-z0-9+.-]+):/i);
  if (schemeMatch) {
    const scheme = schemeMatch[1].toLowerCase();
    if (scheme === 'http' || scheme === 'https' || scheme === 'blob' || scheme === 'cid') {
      return true;
    }
    if (scheme === 'data') {
      // Strictly reject any SVG, XML, HTML, or script references in data URIs
      if (/svg|\+xml|xml|html|script/i.test(trimmed.slice(0, 100))) {
        return false;
      }
      return ALLOWED_DATA_IMAGE_REGEXP.test(trimmed);
    }
    // Any other scheme (javascript, vbscript, file, etc.) is rejected
    return false;
  }

  // No scheme: safe relative URL or path
  return !/[\s<>"'`]/.test(trimmed);
};

export const isAllowedHref = (href) => {
  if (typeof href !== 'string') return false;
  const trimmed = href.trim();
  if (!trimmed) return false;

  const schemeMatch = trimmed.match(/^([a-z0-9+.-]+):/i);
  if (schemeMatch) {
    const scheme = schemeMatch[1].toLowerCase();
    if (scheme === 'http' || scheme === 'https' || scheme === 'mailto' || scheme === 'tel') {
      return true;
    }
    // Disallow data:, blob:, javascript:, vbscript:, etc.
    return false;
  }

  // Safe relative link or hash
  return !/[\s<>"'`]/.test(trimmed);
};

export const DOMPURIFY_CONFIG = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  ALLOWED_URI_REGEXP,
  ALLOW_ARIA_ATTR: false,
  ALLOW_DATA_ATTR: false,
  FORBID_TAGS: ['embed', 'form', 'iframe', 'input', 'link', 'meta', 'object', 'script', 'style', 'svg'],
  SANITIZE_DOM: true,
  SANITIZE_NAMED_PROPS: true
};

// Register DOMPurify attribute hook to enforce strict URI schemes during DOMPurify sanitization
if (typeof DOMPurify?.removeHook === 'function') {
  DOMPurify.removeHook('uponSanitizeAttribute');
}
if (typeof DOMPurify?.addHook === 'function') {
  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    if (data.attrName === 'src') {
      if (!isAllowedMediaSrc(data.attrValue)) {
        data.keepAttr = false;
      }
    } else if (data.attrName === 'href') {
      if (!isAllowedHref(data.attrValue)) {
        data.keepAttr = false;
      }
    } else if (data.attrName === 'data-uploaded-url') {
      if (!isAllowedMediaSrc(data.attrValue)) {
        data.keepAttr = false;
      }
    }
  });
}

const cleanStyles = (root) => {
  root.querySelectorAll('[style]').forEach((element) => {
    const allowedProperties = ['TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'TH', 'TD', 'COL', 'COLGROUP'].includes(element.tagName)
      ? TABLE_STYLE_PROPERTIES
      : BASE_STYLE_PROPERTIES;

    Array.from(element.style).forEach((property) => {
      const value = element.style.getPropertyValue(property);
      if (!allowedProperties.has(property.toLowerCase()) || UNSAFE_STYLE_VALUE.test(value)) {
        element.style.removeProperty(property);
      }
    });

    if (!element.getAttribute('style')?.trim()) element.removeAttribute('style');
  });
};

const cleanClasses = (root, source) => {
  root.querySelectorAll('[class]').forEach((element) => {
    const rawClass = typeof element.className === 'string' ? element.className : '';
    const classes = rawClass.split(/\s+/).filter(Boolean);
    const nextClasses = source === 'paste'
      ? classes.filter((className) => SAFE_CLASSES.has(className))
      : classes.filter((className) => /^[a-zA-Z0-9_-]+$/.test(className));

    if (nextClasses.length) element.className = nextClasses.join(' ');
    else element.removeAttribute('class');
  });
};

const normalizeLinks = (root) => {
  root.querySelectorAll('a').forEach((link) => {
    const href = link.getAttribute('href');
    if (!href || !isAllowedHref(href)) {
      link.removeAttribute('href');
      return;
    }
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  });
};

const normalizeImages = (root, source) => {
  root.querySelectorAll('img').forEach((image) => {
    image.removeAttribute('srcset');
    image.removeAttribute('usemap');

    let src = image.getAttribute('src');
    const uploadedUrl = image.getAttribute('data-uploaded-url');
    let fileKey = image.getAttribute('data-file-key');

    // On paste, never trust pasted data-uploaded-url, data-file-key, or data-screenshot-id
    if (source === 'paste') {
      image.removeAttribute('data-uploaded-url');
      image.removeAttribute('data-screenshot-id');
      image.removeAttribute('data-file-key');
    } else {
      if (uploadedUrl && !isAllowedMediaSrc(uploadedUrl)) {
        image.removeAttribute('data-uploaded-url');
      }
      const screenshotId = image.getAttribute('data-screenshot-id');
      if (screenshotId && !/^[a-zA-Z0-9_-]+$/.test(screenshotId)) {
        image.removeAttribute('data-screenshot-id');
      }
      if (fileKey && !/^[a-zA-Z0-9_./-]+$/.test(fileKey)) {
        image.removeAttribute('data-file-key');
        fileKey = null;
      }
    }

    // Rewrite direct Azure Blob URLs with expiring SAS tokens to permanent canonical proxy URLs
    if (src && source !== 'paste') {
      const extractedKey = extractKeyFromImageUrl(src);
      if (extractedKey && !src.includes('/file/inline/')) {
        const canonicalUrl = buildCanonicalInlineImageUrl(extractedKey);
        image.setAttribute('src', canonicalUrl);
        image.setAttribute('data-file-key', extractedKey);
        src = canonicalUrl;
      }
    }

    // In browser environment (render or stored in DOM), prefer active cached blob URL for zero-flicker render
    const cachedBlob = getCachedInlineBlobUrl(src) || (uploadedUrl ? getCachedInlineBlobUrl(uploadedUrl) : null) || (fileKey ? getCachedInlineBlobUrl(fileKey) : null);
    if (cachedBlob) {
      image.setAttribute('src', cachedBlob);
      src = cachedBlob;
    } else if (src && (source === 'render' || source === 'stored')) {
      const resolved = resolveInlineImageUrl(src);
      if (resolved !== src) {
        image.setAttribute('src', resolved);
        src = resolved;
      }
    }

    if (src && !isAllowedMediaSrc(src)) {
      image.removeAttribute('src');
    }

    const finalSrc = image.getAttribute('src');
    const finalUploadedUrl = image.getAttribute('data-uploaded-url');

    // If an image has neither a valid src nor a valid pending uploadedUrl, remove it
    if (!finalSrc && !finalUploadedUrl) {
      image.remove();
      return;
    }

    if (!image.hasAttribute('alt')) image.setAttribute('alt', '');
  });
};

const normalizeFigures = (root, source) => {
  root.querySelectorAll('figure').forEach((figure) => {
    if (source === 'paste') {
      figure.removeAttribute('data-screenshot-id');
    } else {
      const screenshotId = figure.getAttribute('data-screenshot-id');
      if (screenshotId && !/^[a-zA-Z0-9_-]+$/.test(screenshotId)) {
        figure.removeAttribute('data-screenshot-id');
      }
    }

    const align = figure.getAttribute('data-align')?.toLowerCase();
    if (align) {
      if (['left', 'center', 'right'].includes(align)) {
        figure.setAttribute('data-align', align);
      } else {
        figure.removeAttribute('data-align');
      }
    }

    const widthPercent = figure.getAttribute('data-width-percent');
    if (widthPercent && !/^\d{1,3}$/.test(widthPercent)) {
      figure.removeAttribute('data-width-percent');
    }

    // If it is an inline screenshot container with no image, remove the broken shell
    if (figure.classList.contains('pmpi-inline-screenshot') && !figure.querySelector('img')) {
      figure.remove();
    }
  });
};

const normalizeTables = (root) => {
  root.querySelectorAll('table').forEach((table) => {
    table.classList.add('pmpi-rich-table');
    table.setAttribute('data-pmpi-table', 'true');

    table.querySelectorAll('td, th').forEach((cell) => {
      const align = cell.getAttribute('align')?.toLowerCase();
      if (['left', 'center', 'right', 'justify'].includes(align)) {
        cell.style.textAlign = align;
      }
      cell.removeAttribute('align');
      if (cell.tagName === 'TH' && !cell.hasAttribute('scope')) {
        cell.setAttribute('scope', cell.parentElement?.rowIndex === 0 ? 'col' : 'row');
      }
    });
  });
};

const postProcess = (root, source) => {
  if (source === 'paste') {
    root.querySelectorAll('[contenteditable]').forEach((element) => {
      element.removeAttribute('contenteditable');
    });
  }
  cleanStyles(root);
  cleanClasses(root, source);
  normalizeLinks(root);
  normalizeImages(root, source);
  normalizeFigures(root, source);
  normalizeTables(root);
  return root;
};

export const sanitizeRichTextHtml = (dirtyHtml, { source = 'stored' } = {}) => {
  if (!dirtyHtml) return '';
  const sanitized = DOMPurify.sanitize(String(dirtyHtml), DOMPURIFY_CONFIG);
  if (typeof document === 'undefined') return sanitized;

  const container = document.createElement('div');
  container.innerHTML = sanitized;
  postProcess(container, source);
  return container.innerHTML;
};

export const sanitizeRichTextElement = (root, { source = 'stored' } = {}) => {
  if (!root) return root;
  // The root is the trusted React-owned editor. DOMPurify must still sanitize
  // all of its descendants, but removing this root attribute permanently
  // disables editing because React may not re-apply an unchanged prop.
  const rootContentEditable = root.getAttribute('contenteditable');
  DOMPurify.sanitize(root, { ...DOMPURIFY_CONFIG, IN_PLACE: true });
  if (rootContentEditable !== null) {
    root.setAttribute('contenteditable', rootContentEditable);
  }
  return postProcess(root, source);
};
